import axios from 'axios';

const getBaseURL = () => {
  const apiUrl = import.meta.env.VITE_API_BASE_URL;
  if (!apiUrl || apiUrl === '/api') {
    return '/api';
  }
  return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
};

const API_BASE_URL = getBaseURL();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — inject JWT
api.interceptors.request.use((config) => {
  const authData = localStorage.getItem('auth-storage');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      const token = parsed?.state?.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {}
  }
  return config;
});

// Response interceptor — auto refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authData = localStorage.getItem('auth-storage');
        if (authData) {
          const parsed = JSON.parse(authData);
          const refreshToken = parsed?.state?.refreshToken;

          if (refreshToken) {
            const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });

            // Update stored tokens
            parsed.state.accessToken = data.accessToken;
            parsed.state.refreshToken = data.refreshToken;
            localStorage.setItem('auth-storage', JSON.stringify(parsed));

            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
            return api(originalRequest);
          }
        }
      } catch {
        // Refresh failed — clear auth and redirect
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

// ─── Auth API ─────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  updateProfile: (data: any) => api.patch('/auth/profile', data),
};

// ─── Dashboard API ────────────────────────────────────────
export const dashboardApi = {
  getOverview: () => api.get('/dashboard'),
};

// ─── Leads API ────────────────────────────────────────────
export const leadsApi = {
  list: (params?: any) => api.get('/leads', { params }),
  get: (id: string) => api.get(`/leads/${id}`),
  create: (data: any) => api.post('/leads', data),
  update: (id: string, data: any) => api.put(`/leads/${id}`, data),
  delete: (id: string) => api.delete(`/leads/${id}`),
  stats: () => api.get('/leads/stats'),
};

// ─── Properties API ───────────────────────────────────────
export const propertiesApi = {
  list: (params?: any) => api.get('/properties', { params }),
  get: (id: string) => api.get(`/properties/${id}`),
  create: (data: any) => api.post('/properties', data),
  update: (id: string, data: any) => api.put(`/properties/${id}`, data),
  delete: (id: string) => api.delete(`/properties/${id}`),
  stats: () => api.get('/properties/stats'),
  fromPrivateListing: (id: string) => api.post(`/properties/from-private-listing/${id}`),
  fromIdealistaListing: (id: string) => api.post(`/properties/from-idealista-listing/${id}`),
  fromFotocasaListing: (id: string) => api.post(`/properties/from-fotocasa-listing/${id}`),
};

// ─── Inmovilla API ────────────────────────────────────────
export const inmovillaApi = {
  sync: (propertyId: string) => api.post(`/inmovilla/sync/${propertyId}`),
  unsync: (propertyId: string) => api.post(`/inmovilla/unsync/${propertyId}`),
  syncLead: (leadId: string) => api.post(`/inmovilla/sync-lead/${leadId}`),
  unsyncLead: (leadId: string) => api.post(`/inmovilla/unsync-lead/${leadId}`),
};

export const idealistaApi = {
  sync: (propertyId: string) => api.post(`/idealista/sync/${propertyId}`),
  unsync: (propertyId: string) => api.post(`/idealista/unsync/${propertyId}`),
  reactivate: (propertyId: string) => api.post(`/idealista/reactivate/${propertyId}`),
  findRemote: (idealistaId: number) => api.get(`/idealista/remote/${idealistaId}`),
  findAllRemote: (params?: { page?: number; size?: number; state?: string }) =>
    api.get('/idealista/remote', { params }),
  findImages: (idealistaId: number) => api.get(`/idealista/remote/${idealistaId}/images`),
  putImages: (idealistaId: number, images: Array<{ url: string; label?: string }>) =>
    api.put(`/idealista/remote/${idealistaId}/images`, { images }),
  deleteImages: (idealistaId: number) => api.delete(`/idealista/remote/${idealistaId}/images`),
};

// ─── Opportunities API ────────────────────────────────────
export const opportunitiesApi = {
  list: (params?: any) => api.get('/opportunities', { params }),
  get: (id: string) => api.get(`/opportunities/${id}`),
  create: (data: any) => api.post('/opportunities', data),
  update: (id: string, data: any) => api.put(`/opportunities/${id}`, data),
  delete: (id: string) => api.delete(`/opportunities/${id}`),
  kanban: () => api.get('/opportunities/kanban'),
  stats: () => api.get('/opportunities/stats'),
  match: (id: string) => api.get(`/opportunities/${id}/match`),
};

// ─── Meetings API ─────────────────────────────────────────
export const meetingsApi = {
  list: (params?: any) => api.get('/meetings', { params }),
  get: (id: string) => api.get(`/meetings/${id}`),
  create: (data: any) => api.post('/meetings', data),
  update: (id: string, data: any) => api.put(`/meetings/${id}`, data),
  delete: (id: string) => api.delete(`/meetings/${id}`),
  upcoming: () => api.get('/meetings/upcoming'),
  stats: () => api.get('/meetings/stats'),
};

// ─── Blog API ─────────────────────────────────────────────
export const blogApi = {
  list: (params?: any) => api.get('/blog', { params }),
  get: (id: string) => api.get(`/blog/${id}`),
  create: (data: any) => api.post('/blog', data),
  update: (id: string, data: any) => api.put(`/blog/${id}`, data),
  delete: (id: string) => api.delete(`/blog/${id}`),
  stats: () => api.get('/blog/stats'),
};

// ─── Uploads API ──────────────────────────────────────────
export const uploadsApi = {
  uploadImages: (propertyId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/upload/images?propertyId=${propertyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadVideos: (propertyId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/upload/videos?propertyId=${propertyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFiles: (folder: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/upload/files?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listFiles: (folder: string) => api.get(`/upload/files?folder=${folder}`),
  deleteFile: (filename: string, folder: string) =>
    api.delete(`/upload/files/${filename}?folder=${folder}`),
};

// ─── Site Assets API ──────────────────────────────────────
export const assetsApi = {
  list: (category?: string) =>
    api.get('/assets', { params: category ? { category } : undefined }),
  get: (id: string) => api.get(`/assets/${id}`),
  upload: (file: File, key: string, category: string, alt?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', key);
    formData.append('category', category);
    if (alt) formData.append('alt', alt);
    return api.post('/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id: string, data: { key?: string; alt?: string; category?: string }) =>
    api.put(`/assets/${id}`, data),
  delete: (id: string) => api.delete(`/assets/${id}`),
  publicMap: () => api.get('/assets/public/map'),
};

// ─── Integrations API ─────────────────────────────────────
export const integrationsApi = {
  list: () => api.get('/integrations'),
  get: (key: string) => api.get(`/integrations/${key}`),
  upsert: (key: string, data: { value: any; enabled?: boolean }) =>
    api.put(`/integrations/${key}`, data),
  toggle: (key: string, enabled: boolean) =>
    api.post(`/integrations/${key}/toggle`, { enabled }),
  remove: (key: string) => api.delete(`/integrations/${key}`),
  testSmtp: (to: string) => api.post('/integrations/smtp/test', { to }),
  getGoogleAuthUrl: () => api.post('/integrations/google-calendar/auth-url'),
  googleCalendarCallback: (code: string) =>
    api.post('/integrations/google-calendar/callback', { code }),
};

// ─── AVM API ──────────────────────────────────────────────
export const avmApi = {
  listZones: (params?: any) => api.get('/avm/zones', { params }),
  upsertZone: (zoneId: string, data: any) => api.put(`/avm/zones/${zoneId}`, data),
  bulkUpsertZones: (rows: any[]) => api.post('/avm/zones/bulk-upsert', { rows }),
  importZonesFromText: (text: string) => api.post('/avm/zones/import-text', { text }),
  recomputeAllSalesAverages: () => api.post('/avm/zones/recompute-sales-averages'),
  recomputeAllFinalPrices: () => api.post('/avm/zones/recompute-final-prices'),
  updateFotocasaAverage: (zoneId: string, value: number) =>
    api.post(`/avm/zones/${zoneId}/fotocasa-average`, { value }),
  runFotocasaScraper: (data?: any) => api.post('/avm/apify/run-fotocasa', data || {}),
  runIdealistaScraper: (data?: any) => api.post('/avm/apify/run-idealista', data || {}),
  reprocessDataset: (datasetId: string, zoneId?: string, source?: 'fotocasa' | 'idealista') =>
    api.post('/avm/apify/reprocess-dataset', { datasetId, zoneId, source }),
  // Sales (registros individuales de ventas)
  listSales: (params?: any) => api.get('/avm/sales', { params }),
  createSale: (data: any) => api.post('/avm/sales', data),
  updateSale: (id: string, data: any) => api.put(`/avm/sales/${id}`, data),
  deleteSale: (id: string) => api.delete(`/avm/sales/${id}`),
  // Fotocasa listings (testigos)
  listFotocasaListings: (params?: any) => api.get('/avm/fotocasa-listings', { params }),
  deleteFotocasaListing: (id: string) => api.delete(`/avm/fotocasa-listings/${id}`),
  // Idealista listings (testigos)
  listIdealistaListings: (params?: any) => api.get('/avm/idealista-listings', { params }),
  deleteIdealistaListing: (id: string) => api.delete(`/avm/idealista-listings/${id}`),
  // Cálculos (trazabilidad)
  listCalculations: (params?: any) => api.get('/avm/calculations', { params }),
  getCalculation: (id: string) => api.get(`/avm/calculations/${id}`),
  calculate: (data: any) => api.post('/avm/calculate', data),
  // Sesiones del formulario valorador (funnel analytics)
  getValoradorSessions: (days?: number) =>
    api.get('/avm/valorador-sessions', { params: days ? { days } : {} }),
  // TEMP: emails valorador enviados (cliente + interno)
  listValoradorEmails: (limit?: number) =>
    api.get('/avm/valorador-emails', { params: limit ? { limit } : {} }),
  getValoradorEmail: (id: string) => api.get(`/avm/valorador-emails/${id}`),
  sendTestValoradorEmail: (data: { name: string; email: string }) =>
    api.post('/avm/valorador-emails/test', data),
  // Listado particulares (isAgency: false) — kept for admin backward compat
  listPrivateListings: (params?: any) => api.get('/avm/private-listings', { params }),
  syncPrivateListings: (runId: string) => api.post('/avm/private-listings/sync', { runId }),
};

// ─── Particulares API (accessible with listado-particulares module) ────
export const particularsApi = {
  list: (params?: any) => api.get('/particulares', { params }),
  sync: (runId: string) => api.post('/particulares/sync', { runId }),
  assign: (id: string, userId: string | null) => api.patch(`/particulares/${id}/assign`, { userId }),
  byUser: (userId: string, params?: any) => api.get(`/particulares/by-user/${userId}`, { params }),
};

// ─── Users API ────────────────────────────────────────────
export const usersApi = {
  list: (params?: any) => api.get('/users', { params }),
  agents: () => api.get('/users/agents'),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  deactivate: (id: string) => api.delete(`/users/${id}`),
  updatePermissions: (id: string, modules: string[]) =>
    api.put(`/users/${id}/permissions`, { modules }),
  assignProperties: (id: string, propertyIds: string[]) =>
    api.put(`/users/${id}/assign-properties`, { propertyIds }),
  assignLeads: (id: string, leadIds: string[]) =>
    api.put(`/users/${id}/assign-leads`, { leadIds }),
};

export default api;
